import { useQuery } from '@tanstack/react-query';

import { stockApi } from '@/api/stock';
import type { SellerAccessProfile } from '@/api/stock/sellerWorkspace';
import { useAuth } from '@/hooks/useAuth';
import { queryKeys } from '@/lib/query-client';

/**
 * The profile grants some seller work: a store action (request, receive, assemble) or
 * receipt drafts. Viewing balances alone does not count: every operator gets it by
 * default, so a technician with a camera grant has it too and no use for the workspace.
 */
export function hasSellerWork(profile: SellerAccessProfile | undefined): boolean {
  if (!profile?.is_enabled) return false;
  return (
    profile.can_create_receipt_drafts ||
    profile.locations.some(
      (location) =>
        location.can_request_from ||
        location.can_request_to ||
        location.can_assemble ||
        location.can_receive ||
        location.can_receive_supplier_receipt,
    )
  );
}

export function useSellerWorkspaceAccess() {
  const { user, status } = useAuth({ skipInitialCheck: true });
  const isAdmin = user?.role === 'admin';

  const contextQuery = useQuery({
    queryKey: queryKeys.stock.sellerWorkspace.context(),
    queryFn: () => stockApi.sellerWorkspace.context(),
    enabled: status === 'authenticated' && !isAdmin,
    staleTime: 60_000,
  });

  const profile = contextQuery.data?.profile;
  const isSellerWorkspaceUser = status === 'authenticated' && user !== null && !isAdmin;
  return {
    isSellerWorkspaceUser,
    profile,
    contextQuery,
  };
}
